// ─────────────────────────────────────────────────────────────────────────────
// ConsentContext.tsx
// Hoy Toca Recetas — Grupo Herdez
//
// Estado global de consentimiento de cookies.
// Envuelve la app en index.tsx con <ConsentProvider>.
// Cualquier componente accede al estado con useConsent().
// ─────────────────────────────────────────────────────────────────────────────

import React, {
  createContext,
  useCallback,
  useEffect,
  useState,
} from 'react';

import {
  applyConsent,
  ConsentState,
  defaultConsent,
  loadConsent,
  saveConsent,
} from '../utils/consentManager';

// ── Tipos del contexto ────────────────────────────────────────────────────────

export interface ConsentContextValue {
  /** Estado actual de consentimiento */
  consent: ConsentState;
  /** true mientras no se haya leído localStorage (evita flash del banner) */
  loading: boolean;
  /** Mostrar el banner (false si el usuario ya decidió) */
  showBanner: boolean;
  /** Acepta todas las categorías */
  acceptAll: () => void;
  /** Rechaza todo excepto técnicas */
  rejectAll: () => void;
  /** Guarda selección parcial desde el modal de configuración */
  savePartial: (analytics: boolean, marketing: boolean) => void;
  /** Abre el modal de preferencias (para el link "Configurar cookies" en el footer) */
  openPreferences: () => void;
  /** Si el modal de preferencias está abierto */
  preferencesOpen: boolean;
  /** Cierra el modal de preferencias */
  closePreferences: () => void;
}

// ── Creación del contexto ─────────────────────────────────────────────────────

export const ConsentContext = createContext<ConsentContextValue | null>(null);

// ── Provider ──────────────────────────────────────────────────────────────────

interface ConsentProviderProps {
  children: React.ReactNode;
}

export const ConsentProvider: React.FC<ConsentProviderProps> = ({ children }) => {
  const [consent, setConsent]               = useState<ConsentState>(defaultConsent());
  const [loading, setLoading]               = useState(true);
  const [showBanner, setShowBanner]         = useState(false);
  const [preferencesOpen, setPreferencesOpen] = useState(false);

  // ── Leer localStorage al montar ──────────────────────────────────────────
  useEffect(() => {
    const saved = loadConsent();

    if (saved?.decided) {
      setConsent(saved);
      applyConsent(saved);   // activa scripts si el usuario ya aceptó antes
      setShowBanner(false);
    } else {
      // Primera visita o consent expirado / desactualizado
      setShowBanner(true);
    }

    setLoading(false);
  }, []);

  // ── Persistir y aplicar ───────────────────────────────────────────────────
  const persist = useCallback((next: ConsentState) => {
    saveConsent(next);
    setConsent(next);
    applyConsent(next);
    setShowBanner(false);
    setPreferencesOpen(false);
  }, []);

  // ── Acciones públicas ─────────────────────────────────────────────────────

  const acceptAll = useCallback(() => {
    persist({
      decided:   true,
      technical: true,
      analytics: true,
      marketing: true,
      timestamp: Date.now(),
      version:   consent.version,
    });
  }, [persist, consent.version]);

  const rejectAll = useCallback(() => {
    persist({
      decided:   true,
      technical: true,
      analytics: false,
      marketing: false,
      timestamp: Date.now(),
      version:   consent.version,
    });
  }, [persist, consent.version]);

  const savePartial = useCallback(
    (analytics: boolean, marketing: boolean) => {
      persist({
        decided:   true,
        technical: true,
        analytics,
        marketing,
        timestamp: Date.now(),
        version:   consent.version,
      });
    },
    [persist, consent.version],
  );

  const openPreferences  = useCallback(() => setPreferencesOpen(true),  []);
  const closePreferences = useCallback(() => setPreferencesOpen(false), []);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <ConsentContext.Provider
      value={{
        consent,
        loading,
        showBanner,
        acceptAll,
        rejectAll,
        savePartial,
        openPreferences,
        preferencesOpen,
        closePreferences,
      }}
    >
      {children}
    </ConsentContext.Provider>
  );
};
